import { Button } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import GoogleMapReact, { fitBounds } from "google-map-react";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { GOOGLE_API_KEY } from "common/config";
import { drawPolyline, getBounds } from "common/utils/mapHelper";

import NextSpotMarker from "pages/service/components/NextSpotMarker";
import TruckMarker from "pages/service/components/TruckMarker";
import { operationFilterStore } from "store/slices/operationFilter";
import BranchMarker from "./BranchMarker";

const TAKASUE_BASE_CLASSIFICATION = 4;

const OperationMap = ({
  activeService, listAct, unActiveService, onChangeActive, refetch, autoRefreshMinutes, branches
}) => {
  const [hoveredBranchId, setHoveredBranchId] = useState(null);
  const { showTakasueBranches, showPickupDelivery, showNames } = useSelector(operationFilterStore);

  const visibleBranches = useMemo(
    () =>
      branches.filter((b) => {
        const isTakasue = b.classification === TAKASUE_BASE_CLASSIFICATION;
        if (isTakasue) return showTakasueBranches;
        return showPickupDelivery;
      }),
    [branches, showTakasueBranches, showPickupDelivery]
  );
  const refMapContainer = useRef();
  const refZoomControls = useRef();
  const refAutoRefreshDisplay = useRef();
  const mapRef = useRef(null);
  const [defaultZoom, setDefaultZoom] = useState(10);
  const [currentZoom, setCurrentZoom] = useState(10);
  const [defaultCenter, setDefaultCenter] = useState({
    lat: 34.961872701244076,
    lng: 137.17459801587069
  });

  // Handle fullscreen change - move controls into fullscreen element
  useEffect(() => {
    const handleFullscreenChange = () => {
      const zoomControls = refZoomControls.current;
      const autoRefreshDisplay = refAutoRefreshDisplay.current;

      if (document.fullscreenElement) {
        // Fullscreen active - append elements to fullscreen element
        if (zoomControls) {
          document.fullscreenElement.appendChild(zoomControls);
          zoomControls.classList.add("show");
        }
        if (autoRefreshDisplay) {
          document.fullscreenElement.appendChild(autoRefreshDisplay);
        }
      } else {
        // Exit fullscreen - move elements back
        if (zoomControls) {
          refMapContainer.current?.appendChild(zoomControls);
          zoomControls.classList.remove("show");
        }
        if (autoRefreshDisplay) {
          refMapContainer.current?.appendChild(autoRefreshDisplay);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const lastDeliLat = activeService.last_delivery_act?.latitude;
  const lastDeliLng = activeService.last_delivery_act?.longitude;
  const nextSpotLat = activeService.next_delivery_spot?.latitude;
  const nextSpotLng = activeService.next_delivery_spot?.longitude;

  const drawMap = (map, maps) => {
    mapRef.current = map;

    drawPolyline(
      map,
      maps,
      {
        lat: lastDeliLat,
        lng: lastDeliLng
      },
      {
        lat: nextSpotLat,
        lng: nextSpotLng
      }
    );

    if (listAct.length > 1) {
      listAct.map((item, index) => {
        const { latitude: currentLatitude, longitude: currentLongitude } = item;
        const currentPosition = new maps.LatLng(currentLatitude, currentLongitude);

        if (index < listAct.length - 1) {
          const { latitude: nextLatitude, longitude: nextLongitude } = listAct[index + 1];
          const nextPosition = new maps.LatLng(nextLatitude, nextLongitude);

          new maps.Marker({
            position: currentPosition,
            map,
            icon: {
              path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
              fillColor: "red",
              fillOpacity: 1,
              strokeWeight: 1,
              scale: 4,
              rotation: maps.geometry.spherical.computeHeading(currentPosition, nextPosition)
            }
          });
        }
      });
    }
  };

  const onRefreshData = () => {
    refetch();
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      const newZoom = Math.min(currentZoom + 1, 20);
      mapRef.current.setZoom(newZoom);
      setCurrentZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      const newZoom = Math.max(currentZoom - 1, 1);
      mapRef.current.setZoom(newZoom);
      setCurrentZoom(newZoom);
    }
  };

  useEffect(() => {
    const listLatLng = [...listAct];
    if (lastDeliLat && lastDeliLng) {
      listLatLng.push(activeService.last_delivery_act);
    }
    if (nextSpotLat && nextSpotLng) {
      listLatLng.push(activeService.next_delivery_spot);
    }
    const listLatLngFormat = listLatLng.map((item) => ({
      latitude: item.latitude,
      longitude: item.longitude
    }));

    if (listLatLngFormat.length > 1) {
      const bounds = getBounds(listLatLngFormat);
      if (bounds.ne.lat === bounds.sw.lat && bounds.ne.lng === bounds.sw.lng) {
        setDefaultCenter({
          lat: Number(bounds.ne.lat) || 34.961872701244076,
          lng: Number(bounds.ne.lng) || 137.17459801587069
        });
        setDefaultZoom(10);
      } else {
        const { width, height } = refMapContainer.current?.getClientRects()?.[0] || {};
        const { center, zoom } = fitBounds(bounds, {
          width: width,
          height: height
        });
        setDefaultZoom(zoom);
        setDefaultCenter(center);
      }
    } else {
      setDefaultCenter({
        lat: listLatLngFormat[0]?.latitude || 34.961872701244076,
        lng: listLatLngFormat[0]?.longitude || 137.17459801587069
      });
      setDefaultZoom(10);
    }
  }, [listAct, activeService]);

  useEffect(() => {
    setCurrentZoom(defaultZoom);
  }, [defaultZoom]);

  const handleMapClick = () => {
    setHoveredBranchId(null);
  };

  return (
    <div className="w-full h-full-screen dis-flex" ref={refMapContainer}>
      <Button
        onClick={onRefreshData}
        className="w-112 h-32 pos-absolute update-button"
        type="primary"
      >
        更新
      </Button>
      <div className="zoom-controls" ref={refZoomControls}>
        <button className="zoom-btn" onClick={handleZoomIn}>
          <PlusOutlined />
        </button>
        <button className="zoom-btn" onClick={handleZoomOut}>
          <MinusOutlined />
        </button>
      </div>
      <div className="auto-refresh-display" ref={refAutoRefreshDisplay}>
        自動更新：{autoRefreshMinutes > 0 ? `${autoRefreshMinutes}分` : "しない"}
      </div>
      <GoogleMapReact
        options={{
          streetViewControl: true,
          fullscreenControl: true
        }}
        bootstrapURLKeys={{ key: GOOGLE_API_KEY, libraries: ["geometry"] }}
        zoom={defaultZoom}
        center={defaultCenter}
        key={`${activeService.service_no}
        -${listAct}-${lastDeliLat}-${lastDeliLng}-${nextSpotLat}-${nextSpotLng}`}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => drawMap(map, maps)}
        onClick={handleMapClick}
      >
        {lastDeliLat && lastDeliLng && (
          <TruckMarker
            lat={lastDeliLat}
            lng={lastDeliLng}
            status={"active"}
            data={activeService}
            onChangeActive={onChangeActive}
          />
        )}
        {nextSpotLat && nextSpotLng && <NextSpotMarker lat={nextSpotLat} lng={nextSpotLng} />}
        {unActiveService.length > 0 &&
          unActiveService.map(
            (item) =>
              item?.last_delivery_act?.latitude &&
              item?.last_delivery_act?.longitude && (
                <TruckMarker
                  key={item.service_no}
                  lat={item?.last_delivery_act?.latitude}
                  lng={item?.last_delivery_act?.longitude}
                  status="unactive"
                  onChangeActive={onChangeActive}
                  data={item}
                />
              )
          )}
        {visibleBranches.map(
          (b) =>
            b.latitude != null &&
            b.longitude != null && (
              <BranchMarker
                key={`branch-${b.id}`}
                lat={Number(b.latitude)}
                lng={Number(b.longitude)}
                branch={b}
                isHovered={hoveredBranchId === b.id}
                alwaysShowPopup={showNames}
                onHoverChange={(entered) =>
                  setHoveredBranchId(entered ? b.id : (prev) => (prev === b.id ? null : prev))
                }
              />
            )
        )}
      </GoogleMapReact>
    </div>
  );
};

OperationMap.defaultProps = {
  branches: []
};

OperationMap.propTypes = {
  activeService: PropTypes.object,
  listAct: PropTypes.array,
  unActiveService: PropTypes.array,
  onChangeActive: PropTypes.func,
  refetch: PropTypes.func,
  autoRefreshMinutes: PropTypes.number,
  branches: PropTypes.array
};

export default OperationMap;
